import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faTimes } from "@fortawesome/free-solid-svg-icons";

export interface FilterChip {
    /** Unique identifier for this chip */
    id: string;
    /** Human-readable label shown on the chip */
    label: string;
    /** Called when the user clicks the × on this chip */
    onRemove: () => void;
}

interface FilterBadgeProps {
    /** Number of active filters */
    count: number;
    /** Called when user clicks "Clear all" */
    onClear: () => void;
    /** Individual filter chips with remove buttons */
    chips?: FilterChip[];
    className?: string;
}

/**
 * Reusable filter indicator strip.
 * Shows each applied filter as a removable chip.
 * Renders nothing when count === 0.
 */
const FilterBadge: React.FC<FilterBadgeProps> = ({ count, onClear, chips = [], className = "" }) => {
    if (count === 0) return null;

    return (
        <div
            className={`flex flex-wrap items-center gap-2 px-4 py-2
                bg-blue-50 dark:bg-blue-900/20
                border-b border-blue-200 dark:border-blue-700
                ${className}`}
        >
            <FontAwesomeIcon icon={faFilter} className="text-xs shrink-0 text-blue-600 dark:text-blue-400" />

            {chips.length > 0 ? (
                chips.map(chip => (
                    <span
                        key={chip.id}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium
                            bg-blue-100 dark:bg-blue-800
                            text-blue-800 dark:text-blue-200
                            border border-blue-300 dark:border-blue-600"
                    >
                        {chip.label}
                        <button
                            type="button"
                            onClick={chip.onRemove}
                            className="ml-0.5 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            aria-label={`Remove ${chip.label} filter`}
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                        </button>
                    </span>
                ))
            ) : (
                <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                    {count} active filter{count !== 1 ? "s" : ""}
                </span>
            )}

            <button
                type="button"
                onClick={onClear}
                className="ml-auto flex items-center gap-1 text-xs
                    text-blue-600 dark:text-blue-400
                    hover:text-blue-900 dark:hover:text-blue-100
                    transition-colors rounded"
                title="Clear all filters"
                aria-label="Clear all filters"
            >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
                <span>Clear all</span>
            </button>
        </div>
    );
};

export default FilterBadge;
